import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Tooltip,
  Link,
  Paper,
  Avatar,
  IconButton,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Email,
  Phone,
  WhatsApp,
  LocationCity,
  LocationOn,
  Language,
  ShoppingCart,
  SupportAgent,
  OpenInNew,
  ArrowBack,
  Edit,
  Link as LinkIcon,
  AddShoppingCart,
  MoreVert,
  LockOutlined,
} from "@mui/icons-material";
import ClientPurchaseCard from "./ClientPurchaseCard";
import ClientRelationshipBadge from "../Chat/ClientRelationshipBadge";

const surfaceCardSx = {
  minHeight: 132,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
  width: "100%",
};

export function TabPanel({ children, sx }) {
  return (
    <Box
      className="sidebar-scroll"
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        maxHeight: { xs: "none", md: 560 },
        overflow: "auto",
        bgcolor: (t) => alpha(t.palette.background.default, 0.4),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** Campo label/valor — ocupa `xs` colunas de um grid de 12 (sem MUI Grid aninhado). */
export function InfoField({ label, value, xs = 6 }) {
  const display = value == null || value === "" ? "—" : value;
  const span = Math.min(12, Math.max(1, Number(xs) || 6));

  return (
    <Box
      sx={{
        gridColumn: { xs: "1 / -1", sm: `span ${span}` },
        minWidth: 0,
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.4 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word", lineHeight: 1.45, color: "text.primary" }}>
        {display}
      </Typography>
    </Box>
  );
}

export function InfoFieldsGrid({ children, sx }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(12, minmax(0, 1fr))" },
        gap: 2,
        alignItems: "start",
        width: "100%",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function ContactLinkRow({ icon, href, children, external }) {
  if (!children || children === "—" || children === "Não informado") return null;
  const content = (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
      <Box sx={{ color: "text.secondary", display: "flex", flexShrink: 0 }}>{icon}</Box>
      <Typography
        variant="body2"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontWeight: 500,
          flex: 1,
        }}
      >
        {children}
      </Typography>
      {external && <OpenInNew sx={{ fontSize: 14, opacity: 0.5, flexShrink: 0 }} />}
    </Stack>
  );

  const rowSx = {
    py: 0.75,
    px: 1,
    mx: -1,
    borderRadius: 1.5,
    transition: "background-color 120ms ease",
    "&:hover": href ? { bgcolor: "action.hover" } : {},
  };

  if (!href) return <Box sx={rowSx}>{content}</Box>;

  return (
    <Link
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      underline="none"
      color="inherit"
      sx={{ display: "block", ...rowSx }}
    >
      {content}
    </Link>
  );
}

export function ContactCard({ email, telefone, whatsapp, cidade, estado, website }) {
  return (
    <Box sx={{ ...surfaceCardSx, p: 2 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
        Contato
      </Typography>
      <Stack spacing={0.25} sx={{ mt: 1, flex: 1 }}>
        <Tooltip title="E-mail — atendimento pela plataforma">
          <Box>
            <ContactLinkRow icon={<Email fontSize="small" />}>{email}</ContactLinkRow>
          </Box>
        </Tooltip>
        <Tooltip title="Telefone — atendimento pela plataforma">
          <Box>
            <ContactLinkRow icon={<Phone fontSize="small" />}>{telefone}</ContactLinkRow>
          </Box>
        </Tooltip>
        <Tooltip title="Canal WhatsApp — atendimento pela plataforma">
          <Box>
            <ContactLinkRow icon={<WhatsApp fontSize="small" sx={{ color: "#128C7E" }} />}>
              {whatsapp}
            </ContactLinkRow>
          </Box>
        </Tooltip>
        <ContactLinkRow icon={<LocationCity fontSize="small" />}>{cidade}</ContactLinkRow>
        <ContactLinkRow icon={<LocationOn fontSize="small" />}>{estado}</ContactLinkRow>
        <ContactLinkRow
          icon={<Language fontSize="small" />}
          href={website ? (website.startsWith("http") ? website : `https://${website}`) : null}
          external
        >
          {website}
        </ContactLinkRow>
      </Stack>
    </Box>
  );
}

export function KpiCard({ icon, label, value, hint, footer, accent = "primary", onClick }) {
  const theme = useTheme();
  const color = theme.palette[accent]?.main || theme.palette.primary.main;

  return (
    <Box
      onClick={onClick}
      sx={{
        ...surfaceCardSx,
        p: 2,
        cursor: onClick ? "pointer" : "default",
        transition: "box-shadow 160ms ease, border-color 160ms ease, transform 160ms ease",
        ...(onClick
          ? {
              "&:hover": {
                borderColor: alpha(color, 0.45),
                boxShadow: `0 4px 20px ${alpha(color, 0.12)}`,
              },
            }
          : {}),
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(color, 0.1),
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2, mt: 0.25, letterSpacing: "-0.02em" }}>
            {value}
          </Typography>
          {hint ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {hint}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {footer ? <Box sx={{ pt: 1.5 }}>{footer}</Box> : null}
    </Box>
  );
}

export function RequestsOpenClosedBar({ open, closed }) {
  const total = open + closed;
  const openPct = total ? Math.round((open / total) * 100) : 0;

  return (
    <Box>
      <Box sx={{ height: 5, borderRadius: 99, bgcolor: "action.hover", overflow: "hidden", display: "flex" }}>
        {open > 0 && <Box sx={{ width: `${openPct}%`, bgcolor: "warning.main", minWidth: open ? 6 : 0 }} />}
        {closed > 0 && <Box sx={{ flex: 1, bgcolor: "success.main", opacity: 0.35 }} />}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
        {open} abertas · {closed} fechadas
      </Typography>
    </Box>
  );
}

function EmptyTabState({ icon: Icon, title, description }) {
  return (
    <Box
      sx={{
        py: 6,
        px: 3,
        textAlign: "center",
        borderRadius: 2,
        border: "1px dashed",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      {Icon ? <Icon sx={{ fontSize: 44, color: "text.disabled", mb: 1.5 }} /> : null}
      <Typography variant="subtitle1" color="text.secondary" fontWeight={600}>
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" color="text.disabled" sx={{ mt: 0.75, maxWidth: 360, mx: "auto" }}>
          {description}
        </Typography>
      ) : null}
    </Box>
  );
}

export function ClientPurchaseList({ purchases, onOpen, getOrderCode, buildProductsSummary, buildSaleTypePath, money }) {
  const items = purchases || [];

  if (!items.length) {
    return (
      <EmptyTabState
        icon={ShoppingCart}
        title="Nenhuma compra registrada"
        description='Use "Nova compra" para registrar uma venda comercial para este cliente.'
      />
    );
  }

  const toCard = (p) => {
    if (p.pipelineCard) return p.pipelineCard;
    const typePath = buildSaleTypePath?.(p);
    return {
      id: p.id,
      status: p.status,
      orderCode: getOrderCode?.(p) || `COD-${p.id}`,
      companyName: "Cliente",
      createdAtLabel: p.date || p.data,
      channelType: "Web",
      subtotal: Number(p.amount) || 0,
      shippingCost: 0,
      total: Number(p.amount) || 0,
      items: (p.items || []).map((it) => ({
        name: it.name || it.nome,
        qty: it.qty || it.quantidade || 1,
        sku: it.sku || "",
        price: it.price || it.valor,
      })),
      typePath: typePath !== "—" ? typePath : null,
      product: buildProductsSummary?.(p),
      payment: null,
      shipping: null,
      address: null,
      user: p.user || null,
    };
  };

  return (
    <Stack spacing={1.5}>
      {items.map((p) =>
        p.restricted ? (
          <Paper
            key={p.id}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1.75,
              bgcolor: "action.hover",
              borderColor: "warning.light",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <LockOutlined sx={{ color: "warning.main", mt: 0.25 }} fontSize="small" />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  {p.subject || p.typePath || buildSaleTypePath?.(p) || "Tipo restrito"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Você não tem permissão para visualizar este tipo de solicitação
                </Typography>
                <Chip
                  label="Sem permissão"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ mt: 1, height: 22, fontSize: "0.7rem" }}
                />
              </Box>
            </Stack>
          </Paper>
        ) : (
          <ClientPurchaseCard
            key={p.id}
            card={toCard(p)}
            onClick={() => onOpen?.(p)}
          />
        )
      )}
    </Stack>
  );
}

export function ClientSurveyList({ surveys, onOpen, fmtDate, getSurveyDefinitionId }) {
  const items = surveys || [];

  if (!items.length) {
    return (
      <EmptyTabState
        title="Nenhuma pesquisa respondida"
        description="As respostas de pesquisas vinculadas a este cliente aparecerão aqui."
      />
    );
  }

  return (
    <Stack spacing={1.5}>
      {items.map((s) => {
        const defId = getSurveyDefinitionId(s);
        return (
          <Paper
            key={s.id}
            variant="outlined"
            onClick={defId ? () => onOpen?.(s) : undefined}
            sx={{
              p: 2,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              gap: 2,
              cursor: defId ? "pointer" : "default",
              bgcolor: "background.paper",
              ...(defId ? { "&:hover": { borderColor: "primary.light" } } : {}),
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }} noWrap>
              {s.title || "—"}
            </Typography>
            <Chip size="small" label="Respondida" color="success" sx={{ height: 24 }} />
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              {fmtDate(s.responseDate)}
            </Typography>
          </Paper>
        );
      })}
    </Stack>
  );
}

export function ClientDetailHeader({
  nome,
  nomeSubtitle,
  imagem,
  clientId,
  tipo,
  status,
  statusActive,
  origem,
  tags = [],
  relationships = [],
  onBack,
  onEdit,
  isProcessingEdit,
  onLink,
  externalRegistrationSlot,
  onNewRequest,
  onNewPurchase,
  creatingRel,
  creatingSale,
  downMd,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const visibleTags = tags.slice(0, 5);
  const hiddenTagsCount = Math.max(0, tags.length - visibleTags.length);

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2.5,
        overflow: "hidden",
        mb: 2.5,
        boxShadow: (t) => `0 1px 3px ${alpha(t.palette.common.black, 0.06)}`,
      }}
    >
      {/* Identidade — linha inteira para o nome */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          pt: { xs: 2, md: 2.5 },
          pb: 2,
          background: (t) =>
            `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, transparent 100%)`,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
          <Tooltip title="Voltar para clientes">
            <IconButton size="small" onClick={onBack} sx={{ mt: 0.5, flexShrink: 0 }}>
              <ArrowBack fontSize="small" />
            </IconButton>
          </Tooltip>

          <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Avatar
              src={imagem || undefined}
              sx={{
                width: { xs: 56, md: 64 },
                height: { xs: 56, md: 64 },
                bgcolor: "primary.main",
                fontSize: "1.5rem",
                fontWeight: 700,
                boxShadow: 2,
                border: "2px solid",
                borderColor: "background.paper",
                flexShrink: 0,
              }}
            >
              {imagem ? null : nome?.[0]?.toUpperCase() || "?"}
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant={downMd ? "h6" : "h5"}
                component="h1"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.25,
                  letterSpacing: "-0.02em",
                  wordBreak: "break-word",
                }}
              >
                {nome}
              </Typography>
              {nomeSubtitle ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
                  {nomeSubtitle}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </Stack>

        {/* Meta chips */}
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ pl: { xs: 0, sm: 6.5 } }}>
          <Chip size="small" label={`ID ${clientId || "—"}`} sx={{ bgcolor: "action.selected", fontWeight: 600 }} />
          <Chip size="small" variant="outlined" label={tipo} />
          {relationships.length > 0 ? (
            <ClientRelationshipBadge
              clientId={clientId}
              relationships={relationships}
              count={relationships.length}
            />
          ) : null}
          <Chip
            size="small"
            label={status}
            color={statusActive ? "success" : "default"}
            variant={statusActive ? "filled" : "outlined"}
          />
          {origem != null && origem !== "" && (
            <Chip size="small" variant="outlined" label={`Origem ${origem}`} />
          )}
          {visibleTags.map((t) => (
            <Chip key={t} size="small" label={t} color="primary" variant="outlined" />
          ))}
          {hiddenTagsCount > 0 && <Chip size="small" variant="outlined" label={`+${hiddenTagsCount}`} />}
        </Stack>
      </Box>

      <Divider />

      {/* Barra de ações — secundárias compactas + primárias destacadas */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          bgcolor: (t) => alpha(t.palette.background.default, 0.5),
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          {typeof onEdit === "function" ? (
            <Tooltip title="Editar cadastro">
              <IconButton size="medium" onClick={onEdit} disabled={isProcessingEdit}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          {typeof onLink === "function" ? (
            <Tooltip title="Vincular relacionamento">
              <IconButton size="medium" onClick={onLink}>
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          {externalRegistrationSlot}
          <Tooltip title="Mais ações">
            <IconButton size="medium" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onBack();
              }}
            >
              <ListItemIcon>
                <ArrowBack fontSize="small" />
              </ListItemIcon>
              <ListItemText>Voltar para lista</ListItemText>
            </MenuItem>
            {externalRegistrationSlot ? (
              <Box
                sx={{ px: 1.5, py: 0.75, display: { lg: "none" } }}
                onClick={() => setMenuAnchor(null)}
              >
                {externalRegistrationSlot}
              </Box>
            ) : null}
          </Menu>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SupportAgent />}
            onClick={onNewRequest}
            disabled={creatingRel || creatingSale}
            sx={{ fontWeight: 700, px: 2.5 }}
          >
            {creatingRel ? "Criando..." : "Nova solicitação"}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<AddShoppingCart />}
            onClick={onNewPurchase}
            disabled={creatingRel || creatingSale}
            sx={{ fontWeight: 700, px: 2.5 }}
          >
            {creatingSale ? "Criando..." : "Nova compra"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

// Re-export aliases used by ClientDetails
export { ContactCard as ContactPanel };
export { KpiCard as KpiTile };
export { default as ClientRequestsList } from "./ClientRequestsList";
